import { useState, useEffect, useCallback, useRef } from 'react'
import { TxnModal } from './TxnModal'
import { NovaTransferencia } from './NovaTransferencia'
import { ImportarExtratoCsv } from './ImportarExtratoCsv'
import { DiagnosticoConta } from './DiagnosticoConta'
import { BankLogo } from './BankLogo'
import { brl, ptDate, norm, type Txn, type Kind } from './finance-shared'
import { PlnBarra, PlnBarraRot, PlnBloco } from './PanelShell'
import {
  FiLoader, FiUploadCloud, FiFileText, FiRefreshCw, FiCheckCircle, FiPlusCircle, FiSlash,
  FiRotateCcw, FiTrendingUp, FiTrendingDown, FiInbox, FiAlertTriangle, FiTrash2, FiZap, FiRepeat,
  FiChevronUp, FiChevronDown, FiMoreHorizontal, FiInfo, FiEdit2, FiSearch, FiX, FiClock,
} from 'react-icons/fi'

// Conciliação bancária (PRD §5.1 — Fase 3). UM eixo: movimento OFX ↔ título.
// Painel inline da aba "Conciliações" do Extrato PJ. Espelha a UX guiada já provada
// no crm-twms (ConciliacaoOfx): ação sugerida em destaque, chips-filtro, casamento
// lado a lado banco×título. renderLinha é FUNÇÃO (não componente) pra não remontar e
// perder foco dos inputs — lição do CRM.

type MovStatus = 'pendente' | 'conciliado' | 'ignorado'
interface Candidate { id: number; description: string; party_name: string | null; amount: number; due_date: string; rule: 'exact' | 'approx'; score: number }
interface Suggestion { confianca: 'exata' | 'aproximada' | 'nao_classificada'; category_id: number | null; category_name: string | null; autoCreate: boolean }
interface Mov {
  id: number; type: 'credito' | 'debito'; amount: number; posted_date: string; memo: string | null
  doc_extracted: string | null; status: MovStatus; matched_transaction_id: number | null
  matched_description: string | null; title_auto_created: number; candidates?: Candidate[]
  suggestion?: Suggestion
  bank_account_id: number | null
  // aplicação/resgate (padrão do memo) ou conta própria (CNPJ da empresa no
  // documento). Dinheiro que só mudou de conta — não vira título.
  interno?: 'aplicacao' | 'entre_contas' | null
  // a contraponta já está lançada em N transferências que somam esta linha —
  // dias em que um lado tem várias linhas contra um resgate único do outro
  cobertura_transf?: { transferencias: number; total: number; fecha_exato: boolean } | null
  ja_lancado?: boolean
  // Par pela identidade do Asaas: a integração sabe de qual cobrança é a linha,
  // mesmo quando o valor não bate (o extrato vem com juros/multa por cima).
  asaas_par?: { transaction_id: number; descricao: string; cliente: string
                valor: number; diferenca: number; candidatos: number } | null
  cobertura?: Cobertura | null
  rateio?: Rateio | null      // proposta, quando ainda pendente
  grupo?: Grupo | null        // vínculo gravado, quando já conciliado em rateio
}
interface TituloRateio { id: number; description: string; amount: number; party_name?: string | null }
interface Rateio {
  kind: 'receita' | 'despesa'; bank_account_id: number; posted_date: string; amount: number
  movimentos: Array<{ id: number; posted_date: string; memo: string | null; amount: number; status: string }>
  titulos: TituloRateio[]
}
interface Grupo {
  id: number; amount: number; n_movements: number; n_transactions: number
  created_by: string | null; criado_em: string; note: string | null
  titulos: TituloRateio[]; movimentos: number[]
}
// A linha do extrato é um LOTE: os títulos já estão lançados um a um no ERP e
// somados dão o valor dela. `fecha_exato` = os títulos livres do dia batem
// centavo a centavo com as linhas pendentes do dia.
interface Cobertura { titulos: number; total: number; fecha_exato: boolean }
interface Statement {
  id: number; acctid: string; bank_code: string | null; account_name: string | null
  dtstart: string | null; dtend: string | null; ledgerbal: string | null; dtasof: string | null; n_movements: number
}
interface Counts { pendente: number; conciliado: number; ignorado: number; total: number }
// Janela em que o saldo de um caixa ficou negativo — só existe pra conta de
// dinheiro. `fim: null` significa que ainda está negativo agora.
interface JanelaNegativa { inicio: string; fim: string | null; pior: number; pior_data: string; n: number }
interface HealthConta {
  bank_account_id: number; account_name: string; sistema: number; diverged: boolean; dtasof: string
  // Conta de dinheiro é conferida contra a CONTAGEM FÍSICA, não contra OFX; sem
  // contagem gravada não há com o que comparar e banco/diff/threshold vêm null.
  // 'asaas' = conferida contra o extrato da própria Asaas (asaas_ledger).
  // 'sem_conferencia' = não há fonte externa nenhuma; entra no painel mesmo assim,
  // porque foi sumindo daqui que a conta de Aplicação passou um mês com saldo
  // impossível de -R$ 1,5 mi sem ninguém ver (auditoria 2026-08-04).
  fonte: 'ofx' | 'contagem' | 'sem_contagem' | 'asaas' | 'sem_conferencia'
  saldo_impossivel?: boolean
  possivel_defasagem?: boolean
  defasagem_dias?: number
  banco: number | null; diff: number | null; threshold: number | null
  pendentes: number; pendentes_n: number; resto: number | null; explicada_por_pendentes: boolean
  janelas_negativas: JanelaNegativa[]
}

const STATUS_META: Record<MovStatus, { label: string; cls: string }> = {
  pendente:   { label: 'Pendente',   cls: 'neu-tag bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  conciliado: { label: 'Conciliado', cls: 'neu-tag bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  ignorado:   { label: 'Ignorado',   cls: 'neu-tag bg-secondary text-muted-foreground' },
}
// itens com candidato de título sobem ao topo — são os que pedem julgamento do usuário;
// sort é estável, então mantém a ordem por data dentro de cada grupo
const sortActionableFirst = (list: Mov[]) => [...list].sort((a, b) =>
  ((b.candidates?.length ?? 0) > 0 ? 1 : 0) - ((a.candidates?.length ?? 0) > 0 ? 1 : 0))

const CHIPS: Array<{ k: MovStatus; label: string }> = [
  { k: 'pendente', label: 'Pendentes' },
  { k: 'conciliado', label: 'Conciliados' },
  { k: 'ignorado', label: 'Ignorados' },
]

export function ConciliacaoOfx({ authenticatedFetch, onChanged, focusMovement }: {
  authenticatedFetch: (url: string, opts?: RequestInit) => Promise<Response>
  onChanged: () => void
  // Deep link vindo dos Apontamentos (2026-07-27): abrir a aba JÁ neste
  // movimento, com os candidatos carregados. `token` existe porque clicar duas
  // vezes no mesmo movimento tem que refocar — só o id não muda a referência e
  // o efeito não redispararia. `status` (31/07) diz em qual aba o movimento
  // vive: o conciliado-sem-vínculo é 'conciliado', e sem isso o foco caía
  // sempre em Pendentes — uma aba onde a linha não existe.
  focusMovement?: { id: number; token: number; status?: MovStatus }
}) {
  const [rows, setRows] = useState<Mov[]>([])
  const [counts, setCounts] = useState<Counts>({ pendente: 0, conciliado: 0, ignorado: 0, total: 0 })
  const [lastExtrato, setLastExtrato] = useState<Statement | null>(null)
  // Sino de divergência: saldo do sistema × ledgerbal do OFX, por conta.
  const [health, setHealth] = useState<{ contas: HealthConta[]; alguma_divergente: boolean } | null>(null)
  // Conta cujo detalhamento está aberto. O selo de divergência era só um
  // tooltip: dizia o problema e não oferecia saída (auditoria 2026-08-04).
  const [diagConta, setDiagConta] = useState<number | null>(null)
  const [filter, setFilter] = useState<MovStatus>('pendente')
  // Busca da aba atual (pedido do diretor): achar título/valor/histórico sem Ctrl+F.
  const [search, setSearch] = useState('')
  // Filtro por banco (pedido do diretor 2026-07-22): conferir uma conta por vez.
  // Client-side de propósito — as linhas já estão carregadas e o contador de
  // cada status continua sendo o total, não o do banco escolhido.
  const [bankFilter, setBankFilter] = useState<number | ''>('')
  const [bankMenuOpen, setBankMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [importing, setImporting] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [reprocessing, setReprocessing] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null)
  const [matchSel, setMatchSel] = useState<Record<number, number>>({})
  const [busy, setBusy] = useState<Record<number, boolean>>({})
  // Par extrato × lançamento exibido na conferência (expandida sob o item).
  type MatchData = {
    movement: { id: number; posted_date: string; type: string; amount: number; memo: string | null
      doc_extracted: string | null; fitid: string | null; title_auto_created: number
      bank_account_name: string | null; statement_filename: string | null }
    title: { id: number; kind: string; description: string | null; party_name: string | null; amount: number
      status: string; due_date: string | null; payment_date: string | null; created_at: string | null
      reference_code: string | null; notes: string | null; category_name: string | null
      bank_account_name: string | null } | null
    transfer: { id: number; amount: number; transfer_date: string | null; notes: string | null
      from_account: string | null; to_account: string | null
      suggested_category_id: number | null; suggested_category: string | null } | null
    diff: number | null
    // Rateio da fatura (cartão): partes por categoria + abatimento do total.
    splits?: { items: { category_name: string | null; amount: number; description: string | null }[]
      total: number; rateado: number; sobra: number } | null
  }
  // Expansão inline (o diretor recusou o modal em 2026-07-22: quer conferir sem
  // perder a lista de vista). matchOpen = movimento aberto; o cache evita
  // refazer a busca ao abrir e fechar o mesmo item.
  const [matchOpen, setMatchOpen] = useState<number | null>(null)
  // Pendente COM candidato nasce expandido (pedido do diretor 2026-07-22): ele
  // precisa comparar banco × despesa sem trocar de aba. Guardamos o que foi
  // FECHADO, não o aberto — assim o default é expandido sem precisar semear o
  // estado a cada recarga da lista.
  const [candCollapsed, setCandCollapsed] = useState<Set<number>>(new Set())
  const toggleCand = (id: number) => setCandCollapsed(prev => {
    const n = new Set(prev)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })
  const [matchCache, setMatchCache] = useState<Record<number, { loading: boolean; data: MatchData | null; error?: string }>>({})
  const [creating, setCreating] = useState<Mov | null>(null)
  const [transferring, setTransferring] = useState<Mov | null>(null)
  // Edição inline do título candidato: abre o MESMO modal do Contas em modo edição
  // (PUT /transactions/:id salva categoria + todos os campos correlatos). Evita o
  // vício de criar outra despesa + excluir a antiga só pra trocar a categoria.
  const [editingTxn, setEditingTxn] = useState<Txn | null>(null)
  const [editingKind, setEditingKind] = useState<Kind>('despesa')
  const [editLoading, setEditLoading] = useState<number | null>(null)
  const [showCsv, setShowCsv] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Logo do banco por conta (bank_account_id do movimento → code/name)
  const [banks, setBanks] = useState<Record<number, { code: string | null; name: string; type?: string | null }>>({})
  useEffect(() => {
    authenticatedFetch('/api/bank-accounts').then(r => r.json()).then(d => {
      if (!d.success) return
      const map: Record<number, { code: string | null; name: string; type?: string | null }> = {}
      for (const a of d.items || []) map[a.id] = { code: a.bank_code || null, name: a.name, type: a.type || null }
      setBanks(map)
    }).catch(() => {})
  }, [authenticatedFetch])

  // ── Aprovação em lote ──────────────────────────────────────────────────────
  // A cauda da fila (muitos itens pequenos, mesma decisão) é o que consome o
  // tempo de quem concilia. Selecionar o grupo e resolver de uma vez troca
  // dezenas de cliques por um. Só vale para pendentes.
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [loteCategoria, setLoteCategoria] = useState<number | ''>('')
  const [loteBusy, setLoteBusy] = useState(false)
  const [categorias, setCategorias] = useState<{ id: number; name: string; kind: Kind }[]>([])
  // Categoria do acréscimo de juros/multa das cobranças do Asaas. Uma só pra
  // todas as linhas porque a resposta é contábil, não caso a caso. Pré-escolhe
  // "Receitas Financeiras" se existir — é o destino usual de juros recebidos, e
  // fica à vista num seletor pra quem quiser trocar.
  const [jurosCategoria, setJurosCategoria] = useState<number | ''>('')
  useEffect(() => {
    authenticatedFetch('/api/categories').then(r => r.json()).then(d => {
      if (d.success) {
        const cats = d.categories || []
        setCategorias(cats)
        const fin = cats.find((c: { name: string; kind: Kind }) =>
          c.kind === 'receita' && /receitas?\s+financeiras/i.test(c.name))
        if (fin) setJurosCategoria(fin.id)
      }
    }).catch(() => {})
  }, [authenticatedFetch])

  const toggleSelecionado = (id: number) => setSelecionados(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  // Cada item do lote é independente no backend: os que falham (já tratados,
  // categoria de tipo errado, duplicado) voltam com motivo e a seleção deles é
  // MANTIDA, pra quem aprovou ver o que sobrou em vez de descobrir depois.
  async function aplicarLote(acao: 'criar-titulo' | 'ignorar') {
    if (!selecionados.size || loteBusy) return
    setLoteBusy(true); setMsg(null)
    try {
      const r = await authenticatedFetch('/api/reconciliation/lote', {
        method: 'POST',
        body: JSON.stringify({
          ids: [...selecionados], acao,
          ...(acao === 'criar-titulo' ? { category_id: loteCategoria } : {}),
        }),
      })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha no lote' }); return }
      const falharam: number[] = (d.resultados || []).filter((x: { ok: boolean }) => !x.ok).map((x: { id: number }) => x.id)
      setSelecionados(new Set(falharam))
      const motivos: string[] = [...new Set<string>((d.resultados || [])
        .filter((x: { ok: boolean }) => !x.ok)
        .map((x: { error?: string }) => x.error || 'erro'))]
      setMsg({
        kind: d.falhas ? 'warn' : 'ok',
        text: d.falhas
          ? `${d.ok} de ${d.total} concluídos. ${d.falhas} não deram: ${motivos.join('; ')}`
          : `${d.ok} movimento${d.ok > 1 ? 's' : ''} tratado${d.ok > 1 ? 's' : ''}.`,
      })
      await load()
      onChanged?.()
    } catch {
      setMsg({ kind: 'err', text: 'Erro de conexão no lote' })
    } finally {
      setLoteBusy(false)
    }
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [mv, st, hl] = await Promise.all([
        authenticatedFetch(`/api/reconciliation/movements?status=${filter}`).then(r => r.json()),
        authenticatedFetch('/api/reconciliation/statements').then(r => r.json()),
        authenticatedFetch('/api/reconciliation/health').then(r => r.json()).catch(() => null),
      ])
      if (mv.success) { setRows(sortActionableFirst(mv.rows)); setCounts(mv.counts) }
      if (st.success) setLastExtrato(st.statements[0] || null)
      if (hl?.success) setHealth({ contas: hl.contas, alguma_divergente: hl.alguma_divergente })
    } catch { setMsg({ kind: 'err', text: 'Erro ao carregar conciliação' }) }
    finally { setLoading(false) }
  }, [authenticatedFetch, filter])
  useEffect(() => { load() }, [load])

  const onImport = async (file: File) => {
    setImporting(true); setMsg(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await authenticatedFetch('/api/reconciliation/import', { method: 'POST', body: fd })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha na importação' }); return }
      const e = d.extrato
      const autoBits = []
      if (e.autoConciliados > 0) autoBits.push(`${e.autoConciliados} conciliado(s) automaticamente (100% de confiança)`)
      if (e.autoCriados > 0) autoBits.push(`${e.autoCriados} título(s) criado(s) e baixado(s) sozinho(s) por regra`)
      // autoTransferidos existia na resposta e a tela jogava fora: aplicação e
      // resgate somem da fila sem virar título nem baixa, e quem não vê a
      // contagem conclui que o extrato veio faltando linha.
      if (e.autoTransferidos > 0) autoBits.push(`${e.autoTransferidos} aplicação/resgate lançado(s) como transferência entre contas`)
      const autoTxt = autoBits.length
        ? ` ${autoBits.join('; ')}.`
        : ' Nenhuma automação disparou — confira as regras e o toggle de conciliação automática da conta.'
      const resumo = `${e.novos} novo(s), ${e.dup} já importado(s) de ${e.total} lançamentos.${autoTxt}`
      // O aviso NÃO substitui mais o resumo. Quando a conta não era reconhecida,
      // o if/else antigo mostrava só o aviso e o operador perdia de vista o que
      // a automação fez (ou deixou de fazer) — foi assim que o extrato de 01/08
      // passou sem ninguém notar que nenhuma regra tinha sido aplicada.
      setMsg(d.aviso ? { kind: 'warn', text: `${d.aviso} — ${resumo}` } : { kind: 'ok', text: resumo })
      setFilter('pendente')
      await load()
      onChanged()
    } catch { setMsg({ kind: 'err', text: 'Erro ao enviar o arquivo' }) }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = '' }
  }

  const reprocessar = async () => {
    setReprocessing(true); setMsg(null)
    try {
      const r = await authenticatedFetch('/api/reconciliation/reprocessar', { method: 'POST' })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha ao reprocessar' }); return }
      const autoBits = []
      if (d.autoConciliados > 0) autoBits.push(`${d.autoConciliados} conciliado(s) automaticamente`)
      // Sem esta linha o botão dizia "nada novo pra conciliar" depois de amarrar
      // centenas de linhas em títulos já pagos — contador que não aparece é
      // trabalho que o usuário não vê acontecer.
      if (d.autoHistoricos > 0) autoBits.push(`${d.autoHistoricos} amarrado(s) a título já pago (sem criar nada)`)
      if (d.autoCriados > 0) autoBits.push(`${d.autoCriados} título(s) criado(s) por regra`)
      if (d.autoTransferidos > 0) autoBits.push(`${d.autoTransferidos} lançado(s) como transferência entre contas`)
      const text = autoBits.length
        ? `${d.verificados} pendente(s) verificado(s): ${autoBits.join('; ')}.`
        : `${d.verificados} pendente(s) verificado(s) — nada novo pra conciliar automaticamente.`
      setMsg({ kind: 'ok', text })
      await load(); onChanged()
    } catch { setMsg({ kind: 'err', text: 'Erro de conexão' }) }
    finally { setReprocessing(false) }
  }

  // Duas etapas de propósito. A primeira chamada não apaga nada: volta 400 com
  // a prévia, e é ela que o usuário lê antes de decidir. O confirm genérico que
  // existia aqui ("Limpar TODOS...?") era a única barreira em 10/07 e não
  // informava o tamanho do estrago — dizer "2.658 movimentos, 1.993 deles
  // conciliados" é o que faz alguém parar.
  //
  // Movimento conciliado o backend não apaga, e este botão não oferece a opção
  // de incluir: pela tela, limpeza é para importação que deu errado, não para
  // desfazer conferência feita.
  const limparImports = async () => {
    setClearing(true); setMsg(null)
    try {
      const previaRes = await authenticatedFetch('/api/reconciliation/imports', {
        method: 'DELETE', body: JSON.stringify({}),
      })
      const previaD = await previaRes.json()
      const p = previaD.previa
      if (!p) { setMsg({ kind: 'err', text: previaD.error || 'Falha ao consultar imports' }); return }
      if (p.soltos === 0) {
        setMsg({ kind: 'ok', text: p.conciliados > 0
          ? `Nada a limpar: todos os ${p.conciliados} movimento(s) estão conciliados e são preservados.`
          : 'Nada a limpar.' })
        return
      }
      const aviso = [
        `Apagar ${p.soltos} movimento(s) não conciliado(s) e os extratos que ficarem vazios?`,
        p.conciliados > 0 ? `\n${p.conciliados} movimento(s) conciliado(s) serão PRESERVADOS.` : '',
        '\nTítulos já baixados permanecem como estão. Esta ação não tem volta.',
      ].join('')
      if (!window.confirm(aviso)) return

      const r = await authenticatedFetch('/api/reconciliation/imports', {
        method: 'DELETE', body: JSON.stringify({ confirmar: 'APAGAR-IMPORTS' }),
      })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha ao limpar imports' }); return }
      setMsg({ kind: 'ok', text: `Imports limpos: ${d.movements} movimento(s) e ${d.statements} extrato(s) removidos`
        + (d.preservados > 0 ? `; ${d.preservados} conciliado(s) preservado(s).` : '.') })
      setFilter('pendente')
      await load(); onChanged()
    } catch { setMsg({ kind: 'err', text: 'Erro de conexão' }) }
    finally { setClearing(false) }
  }

  const act = async (id: number, path: string, body?: unknown) => {
    setBusy(b => ({ ...b, [id]: true }))
    try {
      const r = await authenticatedFetch(`/api/reconciliation/${id}/${path}`, {
        method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}),
      })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha na ação' }); return false }
      setMatchSel(s => { const n = { ...s }; delete n[id]; return n }) // some seleção obsoleta
      await load(); onChanged(); return true
    } catch { setMsg({ kind: 'err', text: 'Erro de conexão' }); return false }
    finally { setBusy(b => ({ ...b, [id]: false })) }
  }

  // Grava o rateio: esta linha (mais as irmãs do mesmo dia, se houver) contra
  // todos os títulos que a compõem. O backend recusa se as somas não fecharem,
  // então aqui não há decisão a tomar — só confirmar o que ele já conferiu.
  const conciliarRateio = async (m: Mov) => {
    if (!m.rateio) return
    setBusy(b => ({ ...b, [m.id]: true }))
    try {
      const r = await authenticatedFetch('/api/reconciliation/grupo', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          movement_ids: m.rateio.movimentos.map(x => x.id),
          transaction_ids: m.rateio.titulos.map(t => t.id),
        }),
      })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha ao gravar o rateio' }); return }
      setMsg({ kind: 'ok', text: `Rateio gravado: ${d.n_movements} linha(s) do extrato × ${d.n_transactions} título(s), ${brl(d.amount)}.` })
      await load(); onChanged()
    } catch { setMsg({ kind: 'err', text: 'Erro de conexão' }) }
    finally { setBusy(b => ({ ...b, [m.id]: false })) }
  }

  // Concilia a linha contra um título que JÁ ESTÁ PAGO (o da integração). Rota
  // própria porque /:id/conciliar dá baixa e recusa título pago. Quando o
  // extrato traz juros por cima, o backend lança a diferença como título
  // próprio e grava o conjunto como rateio — ele recusa sem a categoria, então
  // aqui o seletor é obrigatório justamente nesse caso.
  const conciliarHistorico = async (m: Mov) => {
    const par = m.asaas_par
    if (!par) return
    const temDiferenca = par.diferenca > 0.009
    if (temDiferenca && !jurosCategoria) {
      setMsg({ kind: 'err', text: 'Escolha a categoria do acréscimo de juros/multa antes de conciliar.' })
      return
    }
    setBusy(b => ({ ...b, [m.id]: true }))
    try {
      const r = await authenticatedFetch(`/api/reconciliation/${m.id}/conciliar-historico`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: par.transaction_id,
          ...(temDiferenca ? { diferenca_category_id: jurosCategoria } : {}),
        }),
      })
      const d = await r.json()
      if (!d.success) { setMsg({ kind: 'err', text: d.error || 'Falha ao conciliar' }); return }
      setMsg({
        kind: 'ok',
        text: d.diferenca > 0
          ? `Conciliado com o título já pago, e ${brl(d.diferenca)} de juros/multa lançados como título próprio (rateio ${d.group_id}).`
          : 'Conciliado com o título já pago — nada foi criado nem pago de novo.',
      })
      await load(); onChanged()
    } catch { setMsg({ kind: 'err', text: 'Erro de conexão' }) }
    finally { setBusy(b => ({ ...b, [m.id]: false })) }
  }

  // Abre o título candidato pra edição (categoria etc.) sem sair da Conciliação.
  // Hidrata o registro inteiro via GET /transactions/:id porque o candidato só
  // carrega um subconjunto (sem category_id, centro de custo, forma de pgto…).
  const editarCandidato = async (m: Mov, txnId: number) => {
    setEditLoading(txnId)
    try {
      const r = await authenticatedFetch(`/api/transactions/${txnId}`)
      const d = await r.json()
      if (!d.success || !d.transaction) { alert('Não foi possível abrir o título para edição.'); return }
      setEditingKind(m.type === 'credito' ? 'receita' : 'despesa')
      setEditingTxn(d.transaction)
    } catch {
      alert('Erro de conexão ao abrir o título para edição.')
    } finally {
      setEditLoading(null)
    }
  }

  const conciliar = (m: Mov) => {
    const cands = m.candidates || []
    const txnId = matchSel[m.id] ?? cands[0]?.id
    if (!txnId) return
    const rule = cands.find(c => c.id === txnId)?.rule || 'manual'
    act(m.id, 'conciliar', { transaction_id: txnId, rule })
  }

  // ── uma linha de movimento, MODO LISTA compacto (pedido do diretor
  // 2026-07-20 — os cards estavam exagerados). Linha 1: movimento + valor +
  // ações inline; linha 2 (só quando há candidato/vínculo): título casado.
  // Segue FUNÇÃO (não componente) pra preservar o foco do <select>. O botão
  // Prova do casamento (pedido do diretor 2026-07-22): abre o par extrato ×
  // lançamento. Sem isso, "conciliado" é um selo verde em que ele precisa
  // acreditar — e a diferença de valor, quando existe, fica invisível.
  // Abre/fecha SEMPRE pela forma funcional — decide sobre o estado atual do
  // React, nunca sobre o valor capturado no render em que o handler nasceu.
  const toggleMatch = (movementId: number) => {
    setMatchOpen(prev => (prev === movementId ? null : movementId))
    // Busca só na primeira vez. Não depende de estar abrindo ou fechando: se já
    // há cache não faz nada, e é o cache que decide — não um flag de fluxo.
    if (!matchCache[movementId]) void loadMatch(movementId)
  }

  const loadMatch = async (movementId: number) => {
    setMatchCache(c => ({ ...c, [movementId]: { loading: true, data: null } }))
    try {
      const r = await authenticatedFetch(`/api/reconciliation/${movementId}/match`)
      const d = await r.json()
      let data = d.success ? d : null
      // Rateio da fatura (cartão): busca as partes do título conciliado pra
      // mostrar o abatimento — quanto do total já está em categorias e quanto
      // falta aferir. Opcional: título sem rateio ou falha não atrapalha.
      if (data?.title?.id) {
        try {
          const sr = await authenticatedFetch(`/api/transactions/${data.title.id}/splits`)
          const sd = await sr.json()
          if (sd?.success && sd.items?.length) data = { ...data, splits: sd }
        } catch { /* rateio é opcional */ }
      }
      setMatchCache(c => ({ ...c, [movementId]: {
        loading: false, data,
        error: d.success ? undefined : (d.error || 'Falha ao carregar'),
      } }))
    } catch {
      setMatchCache(c => ({ ...c, [movementId]: { loading: false, data: null, error: 'Erro de conexão' } }))
    }
  }

  // Bancos que de fato aparecem nos movimentos carregados — não a lista inteira
  // de contas cadastradas, pra não oferecer filtro que não devolve nada.
  // Contas CADASTRADAS, não as que aparecem nas linhas do status atual: em
  // Pendentes costuma haver um banco só, e derivar da lista fazia o filtro
  // sumir justamente ali — ele tem que existir igual nas três abas.
  const bancosNaLista = Object.entries(banks)
    .map(([id, b]) => ({ id: Number(id), name: b.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

  // Filtro instantâneo da aba: casa a busca contra histórico do banco, título
  // (candidato dos pendentes ou vinculado dos conciliados), favorecido, valor
  // (formatado e cru) e data. Dados da aba já carregados → sem ida ao servidor.
  const matchBusca = (m: Mov) => {
    const q = norm(search)
    if (!q) return true
    const hay = norm([
      m.memo || '', m.matched_description || '',
      String(Math.abs(m.amount)), Math.abs(m.amount).toFixed(2), brl(Math.abs(m.amount)),
      ptDate(m.posted_date),
      ...(m.candidates || []).flatMap(c => [c.description || '', c.party_name || '']),
    ].join(' '))
    return hay.includes(q)
  }
  const visibleRows = rows.filter(r =>
    (bankFilter === '' || r.bank_account_id === bankFilter) && matchBusca(r))

  // ── Foco vindo de fora (Apontamentos → "abrir na conciliação") ────────────
  // Duas etapas de propósito. Primeiro limpa TUDO que poderia esconder a linha
  // (aba, busca, filtro de banco) e abre o par de conferência; só depois, num
  // segundo efeito, rola até ela — a linha pode ainda não existir no DOM
  // enquanto `load()` não voltou, então quem dispara o scroll é a chegada das
  // linhas, não um timer torcendo pelo melhor.
  const [focusId, setFocusId] = useState<number | null>(null)
  useEffect(() => {
    if (!focusMovement) return
    setFilter(focusMovement.status ?? 'pendente')
    setSearch('')
    setBankFilter('')
    setFocusId(focusMovement.id)
    setMatchOpen(focusMovement.id)
    if (!matchCache[focusMovement.id]) void loadMatch(focusMovement.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusMovement])

  useEffect(() => {
    if (focusId == null) return
    const el = document.getElementById(`mov-${focusId}`)
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // O realce sai sozinho: é para achar a linha, não para marcá-la.
    const t = setTimeout(() => setFocusId(null), 2600)
    return () => clearTimeout(t)
  }, [focusId, rows, filter])

  // Comparativo banco × sistema, embutido logo abaixo do movimento. Alinhado
  // com o pl-[120px] das outras linhas secundárias pra não brigar com a coluna
  // de data/histórico.
  function renderMatchInline(movementId: number) {
    const st = matchCache[movementId]
    if (!st || st.loading) {
      return <div className="mt-1 ml-[120px] py-2 text-xs text-muted-foreground"><FiLoader className="w-3 h-3 animate-spin inline" /> carregando…</div>
    }
    if (st.error || !st.data) {
      return <div className="mt-1 ml-[120px] py-2 text-xs text-red-500">{st.error || 'Não foi possível carregar'}</div>
    }
    const { movement: mv, title: ti, transfer: tf, diff } = st.data
    // Rótulo com largura própria e valor logo ao lado. Com justify-between o
    // valor era empurrado pra borda oposta e o bloco parecia esticado, com um
    // vão enorme no meio de cada linha.
    const Campo = ({ k, v }: { k: string; v: React.ReactNode }) => (
      <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5 items-baseline">
        <span className="text-muted-foreground">{k}</span>
        <span className="break-words">{v ?? '—'}</span>
      </div>
    )
    return (
      // max-w: em tela larga o bloco parava de fazer sentido ocupando tudo.
      // Abaixo disso segue flexível e acompanha a largura da lista.
      <div className="neu-convex mt-2 ml-[120px] mb-1.5 max-w-3xl rounded-xl border border-border bg-secondary/30 p-3.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-xs">
          <div>
            <p className="font-semibold mb-1 text-muted-foreground uppercase tracking-wide text-xs">No extrato do banco</p>
            <Campo k="Data" v={ptDate(mv.posted_date)} />
            <Campo k="Valor" v={<strong className={mv.type === 'credito' ? 'text-emerald-600' : 'text-red-600'}>
              {mv.type === 'credito' ? '+' : '−'}{brl(Math.abs(mv.amount))}</strong>} />
            <Campo k="Histórico" v={mv.memo || '—'} />
            <Campo k="Conta" v={mv.bank_account_name} />
            {mv.doc_extracted && <Campo k="Documento" v={mv.doc_extracted} />}
            <Campo k="Arquivo" v={mv.statement_filename} />
          </div>
          <div>
            <p className="font-semibold mb-1 text-muted-foreground uppercase tracking-wide text-xs">
              {tf ? 'Transferência no sistema' : 'Lançamento no sistema'}
            </p>
            {ti ? (
              <>
                <Campo k="Descrição" v={ti.description} />
                <Campo k="Valor" v={<strong>{brl(Number(ti.amount))}</strong>} />
                <Campo k="Tipo" v={ti.kind} />
                <Campo k="Vencimento" v={ti.due_date ? ptDate(ti.due_date) : '—'} />
                <Campo k="Pagamento" v={ti.payment_date ? ptDate(ti.payment_date) : '—'} />
                <Campo k="Categoria" v={ti.category_name} />
                {ti.party_name && <Campo k="Favorecido" v={ti.party_name} />}
                <Campo k="Origem" v={ti.reference_code || (mv.title_auto_created ? 'criado pela conciliação' : 'lançamento manual')} />
              </>
            ) : tf ? (
              <>
                <Campo k="Valor" v={<strong>{brl(Number(tf.amount))}</strong>} />
                <Campo k="Data" v={tf.transfer_date ? ptDate(tf.transfer_date) : '—'} />
                <Campo k="De" v={tf.from_account} />
                <Campo k="Para" v={tf.to_account} />
                {/* Transferência é neutra pro resultado e não tem categoria de
                    verdade. Isto aqui é o rastro de QUAL regra reconheceu a
                    linha — sem ele, resgate de aplicação aparece no relatório
                    como movimento sem classificação e parece regra que não rodou. */}
                {tf.suggested_category && (
                  <Campo k="Reconhecida por" v={<span title="Regra de conciliação que classificou este movimento. Transferência entre contas próprias não entra no DRE.">{tf.suggested_category}</span>} />
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Sem contrapartida encontrada.</p>
            )}
          </div>
        </div>
        {st.data.splits && st.data.splits.items.length > 0 && (() => {
          const s = st.data.splits!
          return (
            <div className="neu-convex mt-3 rounded-lg border border-border bg-background/60 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rateio da fatura</span>
                <span className="text-xs text-muted-foreground tabular-nums">{s.items.length} parte(s)</span>
              </div>
              {s.items.map((p, i) => (
                <div key={i} className="flex items-baseline justify-between gap-2 py-0.5 text-xs">
                  <span className="truncate text-foreground">
                    {p.category_name || '(sem categoria)'}
                    {p.description ? <span className="text-muted-foreground"> · {p.description}</span> : null}
                  </span>
                  <span className="tabular-nums shrink-0 text-red-600 dark:text-red-400">−{brl(Math.abs(Number(p.amount)))}</span>
                </div>
              ))}
              <div className="mt-1 pt-1 border-t border-border/60 flex items-baseline justify-between text-xs">
                <span className="text-muted-foreground">Distribuído</span>
                <span className="tabular-nums">{brl(s.rateado)} de {brl(s.total)}</span>
              </div>
              <div className={`mt-1 rounded px-2 py-1 text-xs ${
                Math.abs(s.sobra) < 0.005 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : s.sobra < 0 ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                {Math.abs(s.sobra) < 0.005 ? 'Rateio fecha com a fatura — nada falta aferir.'
                  : s.sobra < 0 ? `Rateio passou ${brl(Math.abs(s.sobra))} do valor da fatura.`
                  : `Falta aferir ${brl(s.sobra)} — ainda não distribuído em categorias.`}
              </div>
              {s.items.some(p => !p.category_name) && (
                <div className="neu-relief mt-1 rounded px-2 py-1 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300">
                  {s.items.filter(p => !p.category_name).length} parte(s) sem categoria — vaza(m) pra categoria da fatura no DRE.
                </div>
              )}
            </div>
          )
        })()}
        <div className={`mt-2 rounded px-2 py-1 flex items-center gap-1.5 text-xs ${
          diff === null ? 'bg-background text-muted-foreground'
          : diff === 0 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
          {diff === 0 ? <FiCheckCircle className="w-3.5 h-3.5 shrink-0" /> : <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />}
          <span>
            {diff === null ? 'Sem contrapartida para comparar.'
              : diff === 0 ? 'Valores idênticos — banco e sistema batem.'
              : `Diferença de ${brl(Math.abs(diff))} entre o extrato e o lançamento.`}
          </span>
        </div>
      </div>
    )
  }

  // primary é a sugestão do sistema (dica acima da lista explica).
  function renderLinha(m: Mov) {
    const isCredit = m.type === 'credito'
    const abs = Math.abs(m.amount)
    const cands = m.candidates || []
    const selId = matchSel[m.id] ?? cands[0]?.id
    const sel = cands.find(c => c.id === selId)
    const disabled = !!busy[m.id]
    const btnPrimary = 'neu-relief inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground text-xs font-medium px-2.5 py-1.5 hover:opacity-90 disabled:opacity-50'
    const btnBorder = 'neu-convex inline-flex items-center gap-1 rounded-md border border-border text-foreground text-xs font-medium px-2.5 py-1.5 hover:bg-secondary disabled:opacity-50'

    // AÇÃO SUGERIDA (28/07/2026). Antes, a recomendação do sistema era só o
    // destaque visual num dos botões — quem não conhecia a convenção lia três
    // opções igualmente válidas e escolhia pelo nome. Foi assim que 16 lotes
    // viraram 16 títulos novos e R$ 478.228,16 de duplicata na Sicredi.
    // Agora a recomendação tem botão PRÓPRIO, com o nome da ação escrito nele:
    // não exige saber a convenção, e o motivo vai junto no title.
    const sugerida: { acao: 'conciliar' | 'historico' | 'rateio' | 'ignorar' | 'criar' | 'transferencia'; label: string; motivo: string } | null =
      m.status !== 'pendente' ? null
      // ANTES de tudo: linha interna não é título, tenha candidato ou não. Sem
      // este ramo a recomendação em destaque era "Criar título" — foi ela que
      // guiou 18 resgates de junho/2026 para dentro da receita.
      // Interna, mas o outro lado JÁ está lançado em várias transferências que
      // somam esta linha: aqui transferir duplica. A saída é Ignorar, e o
      // motivo sai gravado com os números pelo próprio backend.
      : m.cobertura_transf ? { acao: 'ignorar', label: 'Ignorar',
                motivo: `Esta linha é o agrupado de ${m.cobertura_transf.transferencias} transferências desta conta e data, que já somam ${brl(m.cobertura_transf.total)}${
                  m.cobertura_transf.fecha_exato ? ' e fecham exatamente com ela' : ''}. O dinheiro já está no sistema por elas — lançar outra transferência aqui contaria duas vezes. Ignorar só tira a linha da fila.` }
      : m.interno ? { acao: 'transferencia',
                label: 'Lançar como transferência',
                motivo: m.interno === 'aplicacao'
                  ? 'Aplicação/resgate: o dinheiro só mudou de conta entre a conta corrente e a aplicação. Não é receita nem despesa — como título, infla o resultado do mês. Se a outra ponta já estiver lançada, o sistema vincula nela em vez de criar outra.'
                  : 'O documento deste movimento é o CNPJ da própria empresa: dinheiro entre contas suas, não receita nem despesa. Confira antes — depósito em dinheiro da empresa também cai aqui e pode ser receita de verdade.' }
      : sel ? { acao: 'conciliar', label: 'Conciliar',
                motivo: `Dar baixa no título "${sel.description}" (${brl(sel.amount)}), que já existe em aberto e corresponde a este movimento.` }
      // VEM ANTES de rateio/cobertura/ja_lancado de propósito: aqueles três só
      // sabem dizer "não crie"; este sabe dizer COM O QUE conciliar, porque a
      // identidade vem da própria integração e não de semelhança de valor. A
      // linha de R$ 112,74 do JEFTER era anunciada como "provável lote de 2
      // títulos" quando é uma cobrança de R$ 110,07 + R$ 2,67 de juros.
      : m.asaas_par ? { acao: 'historico',
                label: m.asaas_par.diferenca > 0.009
                  ? `Conciliar com o título já pago (+ ${brl(m.asaas_par.diferenca)} de juros)`
                  : 'Conciliar com o título já pago',
                motivo: `Esta entrada é a cobrança de ${brl(m.asaas_par.valor)} de ${m.asaas_par.cliente}, que a integração do Asaas já lançou e já baixou (título "${m.asaas_par.descricao}"). ${
                  m.asaas_par.diferenca > 0.009
                    ? `Os ${brl(m.asaas_par.diferenca)} que vieram a mais são juros/multa: entram como título próprio na categoria escolhida, e o conjunto é gravado como rateio — a soma fecha com a linha do extrato, sem centavo inventado nem perdido.`
                    : 'Conciliar aqui só registra o vínculo: nada é criado e nada é pago de novo.'}` }
      : m.rateio ? { acao: 'rateio', label: `Conciliar em rateio (${m.rateio.titulos.length} títulos)`,
                motivo: `Esta linha é o pagamento agrupado de ${m.rateio.titulos.length} títulos que já estão lançados e pagos, somando ${brl(m.rateio.amount)}${
                  m.rateio.movimentos.length > 1 ? ` (junto com outras ${m.rateio.movimentos.length - 1} linha(s) do mesmo dia)` : ''}. Conciliar em rateio grava o vínculo com todos eles — fica registrado como conciliado, com os títulos por dentro, e não duplica nada.` }
      : m.cobertura ? { acao: 'ignorar', label: 'Ignorar',
                motivo: `Esta linha é o pagamento agrupado de ${m.cobertura.titulos} títulos que JÁ estão lançados e pagos (somam ${brl(m.cobertura.total)}). O dinheiro já está no sistema — criar título aqui duplicaria. Ignorar só tira a linha da fila.` }
      : m.ja_lancado ? { acao: 'ignorar', label: 'Ignorar',
                motivo: 'Já existe lançamento com esta conta, data e valor — este pagamento já está no sistema. Criar título aqui duplicaria.' }
      : { acao: 'criar', label: 'Criar título',
          motivo: `Não há título correspondente nem pagamento equivalente lançado: este movimento precisa virar um título já pago${
            m.suggestion?.confianca === 'exata' ? ` (categoria sugerida: ${m.suggestion.category_name})` : ''}.` }
    const rodarSugerida = () => {
      if (!sugerida) return
      if (sugerida.acao === 'conciliar') conciliar(m)
      else if (sugerida.acao === 'historico') conciliarHistorico(m)
      else if (sugerida.acao === 'rateio') conciliarRateio(m)
      else if (sugerida.acao === 'ignorar') act(m.id, 'ignorar')
      else if (sugerida.acao === 'transferencia') setTransferring(m)
      else setCreating(m)
    }

    return (
      <div
        key={m.id}
        id={`mov-${m.id}`}
        className={`px-4 py-3 hover:bg-secondary/40 transition-shadow ${
          focusId === m.id ? 'ring-2 ring-primary/60 ring-inset bg-primary/5 rounded-lg' : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {m.bank_account_id && banks[m.bank_account_id] && (
            <span title={banks[m.bank_account_id].name} className="shrink-0 inline-flex">
              <BankLogo code={banks[m.bank_account_id].code} name={banks[m.bank_account_id].name} type={banks[m.bank_account_id].type} size={20} />
            </span>
          )}
          {m.status === 'pendente' && (
            <input
              type="checkbox"
              checked={selecionados.has(m.id)}
              onChange={() => toggleSelecionado(m.id)}
              className="neu-check shrink-0 w-3.5 h-3.5 accent-primary cursor-pointer"
              title="Selecionar para tratar em lote"
            />
          )}
          {isCredit
            ? <FiTrendingUp className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            : <FiTrendingDown className="w-3.5 h-3.5 text-red-500 shrink-0" />}
          <span className="text-xs text-muted-foreground tabular-nums shrink-0 w-[70px]">{ptDate(m.posted_date)}</span>
          <span className="text-sm text-foreground truncate flex-1 min-w-0" title={m.memo || undefined}>{m.memo || '—'}</span>
          {m.doc_extracted && <span className="neu-relief hidden lg:inline text-xs font-medium rounded px-1.5 py-0.5 bg-primary/10 text-primary shrink-0">doc {m.doc_extracted}</span>}
          {/* Na lista de Conciliados o rateio precisa se anunciar: sem etiqueta
              ele é uma linha igual às outras 4.600, e quem procura entender por
              que aquele valor fechou não tem por onde começar. */}
          {m.grupo && (
            <span className="neu-tag text-xs font-medium rounded px-1.5 py-0.5 bg-violet-500/10 text-violet-600 dark:text-violet-400 shrink-0"
              title={`Rateio: esta linha do extrato corresponde a ${m.grupo.n_transactions} títulos do ERP`}>
              rateio ×{m.grupo.n_transactions}
            </span>
          )}
          <span className={`text-sm font-semibold whitespace-nowrap tabular-nums shrink-0 ${isCredit ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {isCredit ? '+' : '−'}{brl(abs)}
          </span>
          {m.status === 'pendente' ? (
            <span className="flex items-center gap-1 shrink-0 ml-1">
              {/* A recomendação vem PRIMEIRO e diz o que faz. As opções abaixo
                  continuam todas disponíveis — isto orienta, não tranca. */}
              {sugerida && (
                <button disabled={disabled} onClick={rodarSugerida}
                  title={`Ação sugerida: ${sugerida.label}.\n\n${sugerida.motivo}`}
                  className={btnPrimary}>
                  {disabled ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiZap className="w-3 h-3" />}
                  Ação sugerida: {sugerida.label}
                </button>
              )}
              {cands.length > 0 && (
                <button disabled={disabled || m.interno === 'aplicacao'} onClick={() => conciliar(m)}
                  title={m.interno === 'aplicacao'
                    ? 'Indisponível: aplicação/resgate não se concilia com título nenhum — use Transferência'
                    : 'Conciliar: dá baixa no título mostrado abaixo'}
                  className={btnBorder}>
                  <FiCheckCircle className="w-3 h-3" /> Conciliar
                </button>
              )}
              {/* Aplicação/resgate não vira título: o botão fica travado aqui e o
                  backend recusa a baixa de qualquer jeito (POST /:id/conciliar).
                  Travar na tela é o que evita o título órfão — o TxnModal CRIA o
                  título antes de conciliar, então recusar só no fim deixaria um
                  lançamento em aberto para trás, que foi como nasceram os 3
                  "APLICACAO FINANCEIRA-CAPTACAO" de julho. */}
              {/* Com par conhecido pela integração, criar título é SEMPRE duplicar —
                  e foi exatamente por aqui que entraram as duplicatas de 20/10
                  (R$ 1.289,77 na mão + R$ 367,50 pela regra). Aviso não bastou:
                  o texto "criar título aqui duplicaria" estava na tela e o botão
                  continuava clicável ao lado. Então aqui ele tranca, igual ao
                  que aplicação/resgate já fazia. */}
              <button disabled={disabled || m.interno === 'aplicacao' || !!m.asaas_par} onClick={() => setCreating(m)}
                title={m.interno === 'aplicacao'
                  ? 'Indisponível: aplicação/resgate é dinheiro que só mudou de conta — use Transferência'
                  : m.asaas_par
                  ? `Indisponível: esta linha é a cobrança de ${brl(m.asaas_par.valor)} de ${m.asaas_par.cliente}, que a integração do Asaas já lançou — criar título aqui duplicaria. Use Ação sugerida: Conciliar com o título já pago.`
                  : m.cobertura
                  ? 'Cuidado: este pagamento parece já estar lançado (veja o aviso abaixo) — criar título aqui duplicaria o valor'
                  : m.suggestion?.confianca === 'exata'
                    ? `Criar título já pago a partir deste movimento (categoria sugerida: ${m.suggestion.category_name})`
                    : 'Criar título já pago a partir deste movimento'}
                className={btnBorder}>
                <FiPlusCircle className="w-3 h-3" /> Criar título
              </button>
              {/* dinheiro que só mudou de conta (saque pra caixinha, PIX entre contas
                  nossas) não é título: como despesa infla o resultado do mês. Mesmo
                  botão da prova real do Extrato. Só aparece com conta identificada —
                  a transferência precisa das duas pontas. */}
              {m.bank_account_id && (
                <button disabled={disabled} onClick={() => setTransferring(m)}
                  title="Lançar como transferência entre as SUAS contas (ex.: saque pra caixinha, PIX entre contas próprias) — escolha só a conta de contrapartida"
                  className={btnBorder}>
                  <FiRepeat className="w-3 h-3 text-violet-500" /> Transferência
                </button>
              )}
              <button disabled={disabled} onClick={() => act(m.id, 'ignorar')}
                title={m.cobertura
                  ? 'Ignorar — o pagamento já está no sistema nos títulos individuais; esta linha do extrato só sai da fila, sem gerar lançamento'
                  : 'Ignorar — tira o movimento da fila'}
                className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50">
                <FiSlash className="w-3.5 h-3.5" />
              </button>
            </span>
          ) : (
            <button disabled={disabled} onClick={() => act(m.id, 'reabrir')} title="Reabrir — volta o movimento pra fila de pendentes"
              className="shrink-0 ml-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground disabled:opacity-50">
              <FiRotateCcw className="w-3 h-3" /> Reabrir
            </button>
          )}
        </div>

        {/* linha 2 — candidato do pendente (compacto) ou vínculo do tratado */}
        {m.status === 'pendente' && sel && (
          <div className="mt-1 pl-[120px] flex items-center gap-2 text-xs text-muted-foreground min-w-0">
            <button type="button" onClick={() => toggleCand(m.id)}
              title={candCollapsed.has(m.id) ? 'Abrir a comparação' : 'Fechar a comparação'}
              className="shrink-0 text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1 hover:underline">
              {candCollapsed.has(m.id) ? <FiChevronDown className="w-3 h-3" /> : <FiChevronUp className="w-3 h-3" />}
              ↔ título:
            </button>
            {cands.length > 1 ? (
              <select value={selId} onChange={e => setMatchSel(s => ({ ...s, [m.id]: Number(e.target.value) }))}
                title="Mais de um título candidato — escolha qual baixar"
                className="neu-convex h-7 max-w-[360px] truncate rounded border border-border bg-background px-1 text-xs text-foreground">
                {cands.map(c => <option key={c.id} value={c.id}>{c.description} — {brl(c.amount)} · {ptDate(c.due_date)} · {c.score}%</option>)}
              </select>
            ) : (
              <span className="truncate" title={sel.description}>{sel.description}</span>
            )}
            <span className="shrink-0 tabular-nums">{brl(sel.amount)} · {ptDate(sel.due_date)}</span>
            <span title={sel.rule === 'exact' ? 'Valor e data conferem' : 'Aproximado — confira antes de confirmar'}
              className={`shrink-0 text-xs font-semibold rounded-full px-1.5 py-px ${
                sel.score >= 90 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : sel.score >= 70 ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                : 'bg-red-500/10 text-red-600 dark:text-red-400'}`}>
              {sel.score}%
            </span>
          </div>
        )}
        {m.status === 'pendente' && sel && !candCollapsed.has(m.id) && (
          <div className="neu-convex mt-2 ml-[120px] mb-1.5 max-w-3xl rounded-xl border border-border bg-secondary/30 p-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-xs">
              <div>
                <p className="font-semibold mb-1 text-muted-foreground uppercase tracking-wide text-xs">No extrato do banco</p>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Data</span><span>{ptDate(m.posted_date)}</span></div>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Valor</span><span className="font-semibold">{brl(Math.abs(m.amount))}</span></div>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Histórico</span><span className="break-words">{m.memo || '—'}</span></div>
                {m.doc_extracted && <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">CPF/CNPJ</span><span>{m.doc_extracted}</span></div>}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-muted-foreground uppercase tracking-wide text-xs">Despesa candidata</p>
                  <button type="button" onClick={() => editarCandidato(m, sel.id)} disabled={editLoading === sel.id}
                    title="Editar este título (categoria, favorecido, valor, datas) sem criar outro nem excluir o antigo"
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50">
                    {editLoading === sel.id ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiEdit2 className="w-3 h-3" />} Editar
                  </button>
                </div>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Vencimento</span><span>{ptDate(sel.due_date)}</span></div>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Valor</span><span className="font-semibold">{brl(sel.amount)}</span></div>
                <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Descrição</span><span className="break-words">{sel.description}</span></div>
                {sel.party_name && <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 py-0.5"><span className="text-muted-foreground">Favorecido</span><span className="break-words">{sel.party_name}</span></div>}
              </div>
            </div>
            {(() => {
              // Por que NÃO conciliou sozinho: a regra automática exige valor e
              // data batendo exatamente. Dizer o que falta evita o diretor abrir
              // a despesa noutra aba só pra descobrir a diferença.
              const dv = Math.round((Math.abs(m.amount) - Number(sel.amount)) * 100) / 100
              const dd = Math.round((new Date(sel.due_date + 'T00:00:00').getTime()
                - new Date(m.posted_date + 'T00:00:00').getTime()) / 86400000)
              const bate = dv === 0 && dd === 0
              return (
                <div className={`mt-2 rounded px-2 py-1 flex items-center gap-1.5 text-xs ${
                  bate ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                       : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                  {bate ? <FiCheckCircle className="w-3.5 h-3.5 shrink-0" /> : <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                  <span>
                    {bate
                      ? 'Valor e data batem — confira o histórico e use Conciliar.'
                      : `Não fechou sozinho: ${dv !== 0 ? `diferença de ${brl(Math.abs(dv))} no valor` : ''}`
                        + `${dv !== 0 && dd !== 0 ? ' e ' : ''}`
                        + `${dd !== 0 ? `vencimento ${Math.abs(dd)} dia(s) ${dd > 0 ? 'depois' : 'antes'} do pagamento` : ''}.`}
                  </span>
                </div>
              )
            })()}
          </div>
        )}
        {/* Par pela integração: mostra DE QUAL cobrança é a linha, e abre o
            destino dos juros ali mesmo — sem isso o backend recusa, e com razão:
            acréscimo sem categoria some do sistema e o saldo deixa de bater com
            o banco. */}
        {m.status === 'pendente' && m.asaas_par && (
          <div className="mt-1 pl-[120px] flex items-start gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
            <FiCheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <b>Esta linha já tem título.</b> É a cobrança de {brl(m.asaas_par.valor)} de{' '}
              {m.asaas_par.cliente}, lançada e baixada pela integração do Asaas
              {m.asaas_par.candidatos > 1 && <> (há {m.asaas_par.candidatos} cobranças iguais dela no mesmo dia — valem a mesma coisa)</>}.
              {m.asaas_par.diferenca > 0.009 ? (
                <>
                  {' '}O extrato traz <b>{brl(m.asaas_par.diferenca)}</b> a mais — juros/multa, que precisam
                  de destino:{' '}
                  <select
                    value={jurosCategoria}
                    onChange={e => setJurosCategoria(e.target.value ? Number(e.target.value) : '')}
                    className="neu-inset rounded px-1 py-0.5 text-xs bg-background text-foreground border border-border align-middle"
                    title="Categoria do acréscimo de juros/multa. Vale para todas as linhas desta fila.">
                    <option value="">escolha a categoria…</option>
                    {categorias.filter(c => c.kind === (isCredit ? 'receita' : 'despesa')).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </>
              ) : (
                <> Os valores batem exatamente: conciliar só registra o vínculo.</>
              )}
            </span>
          </div>
        )}
        {m.status === 'pendente' && !cands.length && !m.asaas_par && m.ja_lancado && (
          <div className="mt-1 pl-[120px] flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <FiAlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              Já existe lançamento com esta conta, data e valor — provavelmente este
              pagamento já está no sistema (o histórico do ERP entra como pago e não
              aparece como título a conciliar). Criar título aqui duplicaria.
            </span>
          </div>
        )}
        {/* Caso AGREGADO (28/07/2026): a linha do banco é um lote — boleto, borderô,
            remessa — e os títulos correspondentes já estão pagos um a um no ERP.
            Nenhum deles tem o valor da linha, então o aviso de valor exato acima
            não dispara e a tela ficava muda. Foi assim que 16 linhas viraram 16
            títulos novos e R$ 478.228,16 de duplicata. Aqui o aviso mostra a
            CONTA — quantos títulos, quanto somam — pra decisão não depender de
            confiança na tela. */}
        {m.status === 'pendente' && !cands.length && !m.asaas_par && m.cobertura && (
          <div className="mt-1 pl-[120px] flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <FiAlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <b>Provável lote — não crie título.</b> Nesta conta e data existem{' '}
              {m.cobertura.titulos} títulos já pagos somando {brl(m.cobertura.total)}, nenhum
              deles amarrado a uma linha do extrato
              {m.cobertura.fecha_exato && <> — e esse total <b>fecha exatamente</b> com as linhas pendentes do dia</>}.
              Este débito é o pagamento agrupado deles: o dinheiro já está no sistema, e
              criar um título aqui duplicaria o valor. Use o botão <b>Ação sugerida: Ignorar</b>.
            </span>
          </div>
        )}
        {m.status === 'conciliado' && m.matched_description && (
          <>
            <button type="button" onClick={() => toggleMatch(m.id)}
              title={matchOpen === m.id ? 'Fechar a conferência' : 'Ver o que veio do banco e com o que foi casado'}
              className="mt-0.5 pl-[120px] w-full flex items-center gap-1 text-left text-xs text-muted-foreground hover:text-foreground">
              {matchOpen === m.id ? <FiChevronUp className="w-3 h-3 shrink-0" /> : <FiChevronDown className="w-3 h-3 shrink-0" />}
              <span className="truncate">Título: {m.matched_description}</span>
            </button>
            {matchOpen === m.id && renderMatchInline(m.id)}
          </>
        )}
        {/* RATEIO: uma linha do extrato que corresponde a vários títulos. Sem
            isto o "Conciliado" seria palavra sem prova — foi exatamente a
            queixa que originou a funcionalidade: o diretor viu um débito de
            R$ 37.936,73 marcado como tratado e não tinha como saber que ele
            era a soma de dez títulos da JVN. A conta fica aberta na tela. */}
        {m.status === 'conciliado' && m.grupo && (
          <>
            <button type="button" onClick={() => toggleMatch(m.id)}
              title={matchOpen === m.id ? 'Fechar o rateio' : 'Ver os títulos que compõem esta linha do extrato'}
              className="mt-0.5 pl-[120px] w-full flex items-center gap-1 text-left text-xs text-muted-foreground hover:text-foreground">
              {matchOpen === m.id ? <FiChevronUp className="w-3 h-3 shrink-0" /> : <FiChevronDown className="w-3 h-3 shrink-0" />}
              <span className="truncate">
                Rateio: {m.grupo.n_transactions} títulos somando {brl(m.grupo.amount)}
                {m.grupo.n_movements > 1 && ` · agrupado com ${m.grupo.n_movements - 1} outra(s) linha(s) do dia`}
              </span>
            </button>
            {/* Trilho à esquerda + recuo: os títulos são FILHOS da linha do
                banco, não vizinhos dela. Sem a marca de hierarquia o bloco lia
                como "mais uma lista solta" no meio dos conciliados. */}
            {matchOpen === m.id && (
              <div className="ml-[132px] mr-2 mt-1 mb-2 border-l-2 border-violet-500/40 pl-3">
                <div className="rounded-lg border border-border bg-muted/30 overflow-hidden">
                  <div className="px-2 py-1 text-xs text-muted-foreground border-b border-border flex items-center justify-between gap-2">
                    <span>{m.grupo.n_transactions} títulos do ERP compõem esta linha do extrato</span>
                    {m.grupo.created_by && <span className="truncate">por {m.grupo.created_by} · {m.grupo.criado_em}</span>}
                  </div>
                  <div className="max-h-56 overflow-y-auto divide-y divide-border/60">
                    {m.grupo.titulos.map(t => (
                      <div key={t.id} className="px-2 py-1 flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground tabular-nums shrink-0 w-12">#{t.id}</span>
                        <span className="truncate flex-1 min-w-0" title={t.party_name || t.description}>
                          {t.description}{t.party_name ? ` — ${t.party_name}` : ''}
                        </span>
                        <span className="tabular-nums shrink-0 font-medium">{brl(t.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="px-2 py-1 text-xs font-medium border-t border-border flex items-center justify-between bg-violet-500/5">
                    <span>Total dos títulos</span>
                    <span className="tabular-nums">{brl(m.grupo.titulos.reduce((s, t) => s + t.amount, 0))}</span>
                  </div>
                  {m.grupo.note && (
                    <div className="px-2 py-1 text-xs text-muted-foreground border-t border-border">{m.grupo.note}</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    // PLANOS (21/10/2026): a aba empilha faixa → aviso → bloco da lista com o
    // respiro de 16px da tela; nada de mb-* entre as peças.
    <div className="flex-1 min-h-0 flex flex-col pln-corpo-tela">
      {/* Uma faixa só: filtros à ESQUERDA, ações à DIREITA. Em blocos separados
          um sobre o outro, importar e filtrar pareciam a mesma família de
          controle. Distância resolve o que empilhar não resolvia. O extrato
          virou legenda fina embaixo — informação, não bloco. */}
      <input ref={fileRef} type="file" accept=".ofx,.qfx" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onImport(f) }} />
      <PlnBarra>
        <PlnBarraRot>Situação</PlnBarraRot>
        {CHIPS.map(c => (
          <button key={c.k} onClick={() => setFilter(c.k)}
            className={`text-sm rounded-lg px-3 py-1.5 border transition-colors ${filter === c.k ? 'neu-latch border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {c.label} <span className="tabular-nums">{counts[c.k]}</span>
          </button>
        ))}
        {/* Vermelho só pro que ninguém explica. Conta cuja diferença é exatamente a
            fila de pendentes está no estado NORMAL de um mês em andamento — o banco
            mexeu, ninguém lançou ainda. Isso é âmbar (trabalho a fazer), não alarme.
            Sem a distinção o painel anunciava "Divergência: R$ 1,39 mi" num mês que
            estava correto, e quem lê de fora entende rombo. */}
        {health && health.contas.length > 0 && (() => {
          // Caixa negativo vem na frente de tudo: não é diferença de conferência,
          // é dinheiro que saiu e nunca entrou no sistema. Foi o que passou 8
          // meses invisível na Caixinha (auditoria 03/08/2026).
          const caixaNeg = health.contas.find(c => c.janelas_negativas?.length)
          const real = health.contas.find(c => c.diverged && !c.explicada_por_pendentes && !c.janelas_negativas?.length && c.diff != null)
          const fila = health.contas.find(c => c.diverged && c.explicada_por_pendentes)
          const semContagem = health.contas.find(c => c.fonte === 'sem_contagem')
          // Saldo impossível vem sem `diff` (não há com o que comparar), então o
          // filtro de `real` acima não o pega — sem esta linha ele cairia no verde
          // "saldo bate com o banco", que é exatamente como ele se escondeu.
          const impossivel = health.contas.find(c => c.saldo_impossivel)
          const tom = (caixaNeg || real || impossivel) ? 'border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400'
            : (fila || semContagem) ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
            : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          // A conta que motiva o alerta — é ela que o painel abre. Ordem igual à
          // do texto abaixo, senão o clique leva a uma conta diferente da que
          // está escrita no selo.
          const alvo = impossivel || caixaNeg || real || fila || semContagem
          return (
            <button type="button" disabled={!alvo}
              onClick={() => alvo && setDiagConta(alvo.bank_account_id)}
              // (havia um 1º title aqui, 'Abrir o detalhamento desta conta', que o
              // segundo sobrescrevia em runtime — tirado porque o tsc recusa atributo duplicado)
              className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium border transition-colors ${tom} ${alvo ? 'hover:brightness-110 cursor-pointer' : 'cursor-default'}`}
              title={health.contas.map(c => {
                if (c.fonte === 'sem_contagem') {
                  return `${c.account_name}: sistema ${brl(c.sistema)} — caixa nunca conferido`
                    + (c.janelas_negativas?.length ? `\n  ${c.janelas_negativas.length} janela(s) com saldo negativo` : '')
                }
                if (c.fonte === 'sem_conferencia') {
                  return `${c.account_name}: sistema ${brl(c.sistema)} — sem extrato nem contagem para conferir`
                    + (c.saldo_impossivel ? '\n  SALDO NEGATIVO NUMA CONTA QUE NÃO PODE FICAR NEGATIVA' : '')
                }
                const externo = c.fonte === 'contagem' ? 'contado' : c.fonte === 'asaas' ? 'Asaas' : 'banco'
                const base = `${c.account_name} (${ptDate(c.dtasof)}): sistema ${brl(c.sistema)} × ${externo} ${brl(c.banco ?? 0)} = diferença ${brl(c.diff ?? 0)}`
                const neg = c.janelas_negativas?.length
                  ? `\n  ${c.janelas_negativas.length} janela(s) com saldo negativo — pior ${
                      brl(Math.min(...c.janelas_negativas.map(j => j.pior)))}`
                  : ''
                const defas = c.possivel_defasagem
                  ? `\n  extrato gerado ${c.defasagem_dias} dia(s) após o fechamento — a diferença pode ser movimento do dia seguinte`
                  : ''
                if (!c.pendentes_n) return base + neg + defas
                return `${base}\n  ${c.pendentes_n} pendente(s) somando ${brl(c.pendentes)}${
                  c.explicada_por_pendentes ? ' — explicam a diferença inteira' : `, sobrando ${brl(c.resto ?? 0)} sem explicação`}${neg}`
              }).join('\n')}>
              {(caixaNeg || real || impossivel) ? <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" />
                : (fila || semContagem) ? <FiClock className="w-3.5 h-3.5 shrink-0" />
                : <FiCheckCircle className="w-3.5 h-3.5 shrink-0" />}
              {impossivel ? `Saldo impossível: ${brl(impossivel.sistema)} em ${impossivel.account_name}`
                : caixaNeg ? `Caixa negativo: ${brl(Math.min(...caixaNeg.janelas_negativas.map(j => j.pior)))} em ${caixaNeg.account_name}`
                : real ? `Divergência: ${brl(Math.abs(real.diff ?? 0))} em ${real.account_name}`
                : fila ? `Faltam lançar ${fila.pendentes_n} pendentes: ${brl(Math.abs(fila.pendentes))} em ${fila.account_name}`
                : semContagem ? `${semContagem.account_name} nunca foi conferido`
                : 'Saldo bate com o banco'}
            </button>
          )
        })()}
        {bancosNaLista.length > 1 && (() => {
          const atual = bankFilter === '' ? null : bancosNaLista.find(b => b.id === bankFilter)
          const escolher = (v: number | '') => { setBankFilter(v); setBankMenuOpen(false) }
          return (
            <div className="relative">
              <button type="button" onClick={() => setBankMenuOpen(o => !o)}
                aria-haspopup="listbox" aria-expanded={bankMenuOpen} title="Filtrar por conta bancária"
                className={`neu-convex h-[34px] inline-flex items-center gap-2 rounded-lg border px-3 text-sm transition-colors ${
                  atual ? 'neu-latch border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                {atual && <BankLogo code={banks[atual.id]?.code || null} name={atual.name} type={banks[atual.id]?.type} size={16} />}
                <span className="truncate max-w-[160px]">{atual ? atual.name : 'Todos os bancos'}</span>
                <FiChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${bankMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {bankMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setBankMenuOpen(false)} />
                  <div role="listbox" aria-label="Contas bancárias"
                    onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setBankMenuOpen(false) } }}
                    className="neu-pop absolute left-0 top-full mt-1 w-60 glass-popover rounded-xl z-40 overflow-hidden py-1">
                    <button type="button" role="option" aria-selected={bankFilter === ''} onClick={() => escolher('')}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary ${bankFilter === '' ? 'text-primary font-medium' : ''}`}>
                      <span className="w-4 shrink-0 text-center">{bankFilter === '' ? '·' : ''}</span>
                      Todos os bancos
                    </button>
                    <div className="max-h-64 overflow-y-auto scrollbar-hidden">
                      {bancosNaLista.map(b => (
                        <button key={b.id} type="button" role="option" aria-selected={bankFilter === b.id}
                          onClick={() => escolher(b.id)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary ${bankFilter === b.id ? 'text-primary font-medium' : ''}`}>
                          <BankLogo code={banks[b.id]?.code || null} name={b.name} type={banks[b.id]?.type} size={16} />
                          <span className="truncate">{b.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )
        })()}

        {/* busca da aba atual — some o Ctrl+F na página */}
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar título, valor, histórico…"
            title="Filtra a lista desta aba por título, valor ou histórico"
            className="h-[34px] w-44 sm:w-56 rounded-lg border border-border bg-background pl-8 pr-7 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          {search && (
            <button type="button" onClick={() => setSearch('')} title="Limpar busca"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* ações — extremo oposto dos filtros */}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => fileRef.current?.click()} disabled={importing}
            className="neu-relief inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-3.5 py-2 hover:opacity-90 disabled:opacity-60">
            {importing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUploadCloud className="w-4 h-4" />} Importar extrato
          </button>
          <div className="relative">
            <button type="button" onClick={() => setMoreOpen(o => !o)}
              aria-haspopup="menu" aria-expanded={moreOpen} title="Mais ações"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary">
              <FiMoreHorizontal className="w-4 h-4" />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMoreOpen(false)} />
                <div role="menu" onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setMoreOpen(false) } }}
                  className="neu-pop absolute right-0 top-full mt-1 w-64 glass-popover rounded-xl z-40 overflow-hidden py-1">
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setShowCsv(true) }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary">
                    <FiFileText className="w-4 h-4" /> Importar CSV
                  </button>
                  <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); load() }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary">
                    <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar lista
                  </button>
                  <button type="button" role="menuitem" disabled={reprocessing || counts.pendente === 0}
                    onClick={() => { setMoreOpen(false); reprocessar() }}
                    title="Roda a auto-conciliação e a auto-criação por regra sobre os pendentes atuais"
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-secondary disabled:opacity-40">
                    {reprocessing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiZap className="w-4 h-4" />} Reprocessar pendentes
                  </button>
                  <div className="my-1 border-t border-border" />
                  <button type="button" role="menuitem" disabled={clearing || importing || counts.total === 0}
                    onClick={() => { setMoreOpen(false); limparImports() }}
                    title="Remove todos os extratos e movimentos importados (títulos já baixados permanecem)"
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-600 dark:text-red-400 hover:bg-red-500/10 disabled:opacity-40">
                    {clearing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiTrash2 className="w-4 h-4" />} Limpar imports
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </PlnBarra>

      {/* Uma linha só pra tudo que é apoio: extrato à esquerda, e a dica da UX
          guiada virou ícone com tooltip — texto de ajuda não precisa de faixa
          permanente depois que a pessoa aprendeu o fluxo. */}
      {msg && (
        <div className={`shrink-0 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
          msg.kind === 'ok' ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300'
          : msg.kind === 'warn' ? 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300'
          : 'border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400'}`}>
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{msg.text}</span>
        </div>
      )}

      {/* Lista de movimentos — única região com scroll. PLANOS (21/10/2026):
          vira um bloco com título; a legenda do último extrato e a dica "como
          funciona" (antes uma linha de 11px solta acima da lista) moram no
          cabeçalho dele, e a barra de lote fica fixa entre o cabeçalho e a lista. */}
      <PlnBloco
        className="flex-1 min-h-[280px]"
        titulo={`Movimentos ${CHIPS.find(c => c.k === filter)?.label.toLowerCase() ?? ''}`}
        nota={loading ? undefined : (
          <span className="inline-flex flex-wrap items-center gap-x-1.5">
            <span className="tabular-nums">{visibleRows.length} movimento{visibleRows.length === 1 ? '' : 's'}</span>
            {lastExtrato && (
              <span className="truncate">
                <span className="mx-1 opacity-40">·</span>último extrato: {lastExtrato.account_name || lastExtrato.acctid}
                <span className="mx-1.5 opacity-40">·</span>{ptDate(lastExtrato.dtstart)}–{ptDate(lastExtrato.dtend)}
                {lastExtrato.ledgerbal != null && (<><span className="mx-1.5 opacity-40">·</span>saldo <span className="font-medium text-foreground">{brl(lastExtrato.ledgerbal)}</span></>)}
              </span>
            )}
          </span>
        )}
        extra={filter === 'pendente' && counts.pendente > 0 ? (
          <span className="inline-flex items-center gap-1.5 shrink-0 cursor-help text-[13px] text-muted-foreground"
            title="Cada pendente traz um botão 'Ação sugerida' com o nome do que ele faz e o motivo no tooltip — na dúvida, é ele. As outras opções seguem à mão: Conciliar dá baixa num título existente; Criar título lança um novo já pago; Ignorar tira o movimento da fila (é o certo quando a linha é um lote de títulos já lançados).">
            <FiInfo className="w-4 h-4" /> como funciona
          </span>
        ) : undefined}
      >
        {!loading && visibleRows.length > 0 && filter === 'pendente' && (
          <div className="shrink-0 flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border/60 text-[13px]">
            <button
              type="button"
              onClick={() => setSelecionados(prev =>
                prev.size === visibleRows.length ? new Set() : new Set(visibleRows.map(r => r.id)))}
              className="neu-convex h-8 px-3 rounded-lg border border-border hover:bg-muted/50"
              title="Marcar ou desmarcar todos os movimentos visíveis (respeita o filtro de banco e a busca)"
            >
              {selecionados.size === visibleRows.length && visibleRows.length > 0 ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>
            {selecionados.size > 0 && (
              <>
                <span className="text-muted-foreground">
                  {selecionados.size} selecionado{selecionados.size > 1 ? 's' : ''} ·{' '}
                  {brl(visibleRows.filter(r => selecionados.has(r.id)).reduce((s, r) => s + Math.abs(r.amount), 0))}
                </span>
                <select
                  value={loteCategoria}
                  onChange={e => setLoteCategoria(e.target.value ? Number(e.target.value) : '')}
                  className="neu-convex h-8 px-2 rounded-lg border border-border bg-background"
                  title="Categoria dos títulos que serão criados. Só vale para movimentos do mesmo tipo (entrada ou saída) da categoria."
                >
                  <option value="">categoria…</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.kind === 'receita' ? '↑' : '↓'} {c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={loteBusy || !loteCategoria}
                  onClick={() => aplicarLote('criar-titulo')}
                  className="neu-relief inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                  title="Cria um título pago para cada movimento selecionado, na categoria escolhida"
                >
                  {loteBusy ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiPlusCircle className="w-3.5 h-3.5" />} Criar títulos
                </button>
                <button
                  type="button"
                  disabled={loteBusy}
                  onClick={() => aplicarLote('ignorar')}
                  className="neu-convex inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-border hover:bg-muted/50 disabled:opacity-50"
                  title="Marca os selecionados como ignorados — eles saem da fila mas continuam no extrato"
                >
                  <FiSlash className="w-3.5 h-3.5" /> Ignorar
                </button>
              </>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin" /></div>
          ) : visibleRows.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">
                <FiInbox className="w-5 h-5 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground">
                {/* Com filtro de banco ativo a lista pode estar vazia mesmo
                    havendo movimentos — dizer "importe um extrato" aqui mandaria
                    o diretor importar de novo o que já está lá. */}
                {search.trim()
                  ? `Nenhum movimento ${STATUS_META[filter].label.toLowerCase()} para “${search.trim()}”.`
                  : bankFilter !== '' && rows.length > 0
                  ? `Nenhum movimento ${STATUS_META[filter].label.toLowerCase()} nesta conta.`
                  : filter === 'pendente' ? 'Nenhum movimento pendente. Importe um extrato OFX para começar.'
                  : `Nenhum movimento ${STATUS_META[filter].label.toLowerCase()}.`}
              </p>
            </div>
          ) : (
            /* modo lista: linhas densas (divide-y) direto no papel do bloco, sem cards */
            <div className="divide-y divide-border/60">{visibleRows.map(renderLinha)}</div>
          )}
        </div>
      </PlnBloco>

      {creating && (
        // Modal COMPLETO de despesa/receita (mesmo do Contas a pagar — pedido do
        // diretor 2026-07-10), pré-preenchido com o máximo do movimento: memo,
        // valor, datas, conta e categoria sugerida. O título nasce em aberto
        // (lockPaid esconde a baixa manual) e a conciliação dá a baixa na
        // sequência com a data do movimento — mesmas garantias do fluxo antigo.
        <TxnModal
          kind={creating.type === 'credito' ? 'receita' : 'despesa'}
          defaults={{
            description: creating.memo || '',
            amount: Math.abs(creating.amount),
            date: (creating.posted_date || '').slice(0, 10),
            bank_account_id: creating.bank_account_id || undefined,
            category_id: creating.suggestion?.confianca === 'exata' ? (creating.suggestion.category_id ?? undefined) : undefined,
            lockPaid: true,
            from_bank_movement: true,
          }}
          onClose={() => setCreating(null)}
          onSaved={() => { setCreating(null); load(); onChanged() }}
          onCreated={async ids => {
            const mov = creating
            setCreating(null)
            try {
              const r = await authenticatedFetch(`/api/reconciliation/${mov.id}/conciliar`, {
                method: 'POST', body: JSON.stringify({ transaction_id: ids[0] }),
              })
              const d = await r.json()
              if (!d.success) alert(`Título criado, mas a conciliação falhou: ${d.error || 'erro'}. Ele aparece nos candidatos — concilie manualmente.`)
            } catch {
              alert('Título criado, mas a conciliação falhou por erro de conexão. Ele aparece nos candidatos — concilie manualmente.')
            }
            load(); onChanged()
          }}
        />
      )}

      {editingTxn && (
        // Edição do título candidato — MESMO modal do Contas (modo edição). O PUT
        // /transactions/:id salva categoria + favorecido + valor + datas de uma vez;
        // nada de criar outro e apagar. Ao salvar, recarrega a fila pra refletir.
        <TxnModal
          kind={editingKind}
          txn={editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={() => { setEditingTxn(null); load(); onChanged() }}
        />
      )}

      {transferring && (
        // Débito: sai da conta do extrato. Crédito: entra nela. A ponta oposta é
        // a única escolha do usuário — o backend recusa se o sinal não bater.
        <NovaTransferencia
          movementId={transferring.id}
          {...(transferring.type === 'debito'
            ? { defaultFromId: transferring.bank_account_id! }
            : { defaultToId: transferring.bank_account_id! })}
          defaultDate={(transferring.posted_date || '').slice(0, 10)}
          defaultAmount={Math.abs(transferring.amount)}
          defaultDescription={transferring.memo || 'Transferência (extrato do banco)'}
          onClose={() => setTransferring(null)}
          onSaved={(aviso) => {
            setTransferring(null); load(); onChanged()
            // "vinculado à transferência que já existia" tem que aparecer: sem
            // isso o operador não distingue vincular de criar outra linha.
            if (aviso) setMsg({ kind: 'ok', text: aviso })
          }}
        />
      )}

      {showCsv && (
        <ImportarExtratoCsv authenticatedFetch={authenticatedFetch}
          onClose={() => setShowCsv(false)}
          onImported={() => { setFilter('pendente'); load(); onChanged() }} />
      )}

      {diagConta != null && (
        <DiagnosticoConta
          accountId={diagConta}
          onClose={() => setDiagConta(null)}
          onCorrigido={() => { load(); onChanged() }}
        />
      )}

    </div>
  )
}

// (ActionBtn removido em 2026-07-20 — o modo lista compacto usa botões inline.
// CriarTituloModal removido em 2026-07-10 — o "Criar título" abre o TxnModal
// completo pré-preenchido e concilia na sequência, ver uso acima. O endpoint
// /criar-titulo segue existindo pro auto-create por regra.)
